import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Divider,
  Paper,
  Autocomplete
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';

interface PartiesTabProps {
  data: any;
  onChange: (data: any) => void;
}

const PartiesTab: React.FC<PartiesTabProps> = ({ data, onChange }) => {
  const { t, tString } = useTranslation();
  const handlePartyChange = (party: string, field: string, value: any) => {
    onChange({
      ...data,
      [party]: {
        ...data[party],
        [field]: value
      }
    });
  };

  const renderPartySection = (
    title: string,
    partyKey: string,
    description: string
  ) => {
    const party = data[partyKey] || {};

    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company Name"
              value={party.name || ''}
              onChange={(e) => handlePartyChange(partyKey, 'name', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="EORI Number"
              value={party.eori || ''}
              onChange={(e) => handlePartyChange(partyKey, 'eori', e.target.value)}
              helperText="Economic Operators Registration and Identification"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="VAT Number"
              value={party.vat || ''}
              onChange={(e) => handlePartyChange(partyKey, 'vat', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={party.street || ''}
              onChange={(e) => handlePartyChange(partyKey, 'street', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={party.city || ''}
              onChange={(e) => handlePartyChange(partyKey, 'city', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Postal Code"
              value={party.postal_code || ''}
              onChange={(e) => handlePartyChange(partyKey, 'postal_code', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Country Code"
              value={party.country || ''}
              onChange={(e) => handlePartyChange(partyKey, 'country', e.target.value)}
              placeholder="e.g., DE, FR, US"
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Person"
              value={party.contact_name || ''}
              onChange={(e) => handlePartyChange(partyKey, 'contact_name', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              value={party.phone || ''}
              onChange={(e) => handlePartyChange(partyKey, 'phone', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={party.email || ''}
              onChange={(e) => handlePartyChange(partyKey, 'email', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box>
      <SectionHeader
        title={t('customsDeclaration.parties.title') as string}
        subtitle={t('customsDeclaration.parties.subtitle') as string}
        icon={<GroupIcon color="primary" sx={{ fontSize: 32 }} />}
      />

      {renderPartySection(
        'Exporter',
        'exporter',
        'Party shipping the goods out of the country'
      )}

      {renderPartySection(
        'Importer',
        'importer',
        'Party receiving the goods in the destination country'
      )}

      {renderPartySection(
        'Declarant',
        'declarant',
        'Party making the customs declaration (may be same as exporter/importer or a customs agent)'
      )}

      {renderPartySection(
        'Representative',
        'representative',
        'Customs broker or agent representing the declarant (optional)'
      )}
    </Box>
  );
};

export default PartiesTab;
